import { compose, createSingletonMixin } from '@descope/sdk-helpers';
import { injectNpmLibMixin } from '../injectNpmLibMixin';
import { loggerMixin } from '../loggerMixin';
import { TelemetryConfig } from '../configMixin/types';
import {
  DEBUG_LOGS_LIB_NAME,
  JS_FILE_PATH,
  LOCAL_STORAGE_OVERRIDE,
} from './constants';
import { initLifecycleMixin } from '../initLifecycleMixin';
import { configMixin } from '../configMixin';
import { projectIdMixin } from '../projectIdMixin';
import { flowIdMixin } from '../flowIdMixin';

// Human-readable delay formatter. Picks the largest unit that fits so logs
// read as "30 minutes" / "2 days" instead of "1800 seconds" / "172800 seconds".
function formatDelay(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} seconds`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minutes`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hours`;
  return `${Math.floor(h / 24)} days`;
}

// Extend global window to include DescopeDebugLogs (UMD global)
declare global {
  interface Window {
    DescopeDebugLogs?: new (config: any, context: any, logger?: any) => any;
  }
}

export const telemetryMixin = createSingletonMixin(
  <T extends CustomElementConstructor>(superclass: T) => {
    const BaseClass = compose(
      injectNpmLibMixin,
      loggerMixin,
      initLifecycleMixin,
      configMixin,
      projectIdMixin,
      flowIdMixin,
    )(superclass);

    return class TelemetryMixinClass extends BaseClass {
      #telemetryManager: any = null;
      #telemetryInitialized = false;
      #expirationTimeoutId: NodeJS.Timeout | null = null;

      async #initializeTelemetry(
        config: TelemetryConfig,
        context: {
          projectId: string;
          flowId?: string;
          sdkVersion?: string;
        },
      ) {
        if (!config?.enabled) {
          this.logger.debug('Telemetry is disabled, skipping initialization');
          return;
        }

        if (this.#telemetryInitialized) {
          this.logger.debug('Telemetry already initialized');
          return;
        }

        if (config.expiration && Date.now() >= config.expiration) {
          this.logger.info(
            `Telemetry expiration time has passed (${new Date(
              config.expiration,
            ).toISOString()}), skipping initialization`,
          );
          return;
        }

        try {
          this.logger.debug('Initializing telemetry...');
          const version = config.version || 'latest';

          await this.injectNpmLib(DEBUG_LOGS_LIB_NAME, version, JS_FILE_PATH, [
            LOCAL_STORAGE_OVERRIDE,
          ]);

          const TelemetryManager = window.DescopeDebugLogs;
          if (!TelemetryManager) {
            throw new Error(
              'DescopeDebugLogs not found after loading @descope/debug-logs. ' +
                'This likely means the UMD build failed to load from CDN.',
            );
          }

          this.#telemetryManager = new TelemetryManager(
            {
              enabled: true,
              rumConfig: config.rumConfig,
              capture: config.capture,
            },
            {
              projectId: context.projectId,
              flowId: context.flowId,
              version: context.sdkVersion || '1.0.0',
            },
            this.logger,
          );

          // The TelemetryManager constructor swallows RUM-client init errors
          // (it sets an internal failure flag instead of throwing). Confirm it
          // actually came up before flagging success - otherwise we'd log
          // "initialized" and, because #initializeTelemetry early-returns once
          // #telemetryInitialized is true, permanently block any retry.
          if (!this.#telemetryManager.isReady()) {
            this.logger.error(
              'Telemetry manager failed to initialize (RUM client not ready)',
            );
            this.#telemetryManager = null;
            return;
          }

          this.#telemetryInitialized = true;

          this.logger.info('Telemetry initialized successfully');

          if (config.expiration) {
            this.#scheduleExpirationShutdown(config.expiration);
          }
        } catch (error) {
          this.logger.error('Failed to initialize telemetry:', error);
          this.#telemetryInitialized = false;
        }
      }

      /**
       * Schedule automatic telemetry shutdown at expiration time
       * @param expirationMs - Unix timestamp in milliseconds when telemetry should expire
       */
      #scheduleExpirationShutdown(expirationMs: number) {
        const now = Date.now();
        const timeUntilExpiration = expirationMs - now;

        if (timeUntilExpiration <= 0) {
          this.logger.info('Telemetry expiration time reached, shutting down');
          this.#shutdownTelemetry();
          return;
        }

        // setTimeout's max safe delay is ~24.8 days; cap at 1 day so a
        // mis-set expiration far in the future doesn't underflow to 0.
        const MAX_TIMEOUT = 86400000;
        const delay = Math.min(timeUntilExpiration, MAX_TIMEOUT);

        if (timeUntilExpiration > MAX_TIMEOUT) {
          this.logger.warn(
            `Telemetry expiration is beyond maximum timeout (${formatDelay(
              timeUntilExpiration,
            )}). Setting to maximum.`,
          );
        }

        this.logger.info(
          `Telemetry will expire at ${new Date(
            expirationMs,
          ).toISOString()} (in ${formatDelay(delay)})`,
        );

        this.#expirationTimeoutId = setTimeout(() => {
          this.logger.info('Telemetry expiration time reached, shutting down');
          this.#shutdownTelemetry();
        }, delay);
      }

      #shutdownTelemetry() {
        if (this.#expirationTimeoutId) {
          clearTimeout(this.#expirationTimeoutId);
          this.#expirationTimeoutId = null;
        }

        if (this.#telemetryManager) {
          try {
            this.logger.debug('Shutting down telemetry...');
            this.#telemetryManager.shutdown();
            this.#telemetryManager = null;
            this.#telemetryInitialized = false;
            this.logger.info('Telemetry shutdown complete');
          } catch (error) {
            this.logger.error('Error shutting down telemetry:', error);
          }
        }
      }

      /**
       * Get the telemetry manager instance (for advanced usage)
       */
      get telemetryManager() {
        return this.#telemetryManager;
      }

      // A RUM client needs applicationId, identityPoolId and region. If any is
      // missing, AwsRum either throws at construction or silently records
      // nothing, so we treat the config as unusable.
      #isRumConfigComplete(rumConfig?: TelemetryConfig['rumConfig']): boolean {
        return Boolean(
          rumConfig?.applicationId &&
            rumConfig?.identityPoolId &&
            rumConfig?.region,
        );
      }

      async #getTelemetryConfig(): Promise<TelemetryConfig> {
        // Prefer telemetry config shipped by the backend in the project's
        // config.json. Otherwise, fall back to values from
        // packages/sdks/web-component/.env (DESCOPE_TELEMETRY_* — substituted
        // at WC build time via rollup.config.app.mjs). We MERGE rather than
        // replace so the backend can override individual fields (e.g. just
        // expiration) without re-stating every credential.
        // The backend ships telemetry nested under `projectConfig` in config.json.
        const resolvedConfig = await this.config;
        const beTelemetry = resolvedConfig?.projectConfig?.telemetry;

        const sampleRate = Number(
          process.env.DESCOPE_TELEMETRY_SESSION_SAMPLE_RATE,
        );

        const fallback: NonNullable<TelemetryConfig> = {
          enabled:
            (process.env.DESCOPE_TELEMETRY_ENABLED || '').toLowerCase() ===
            'true',
          rumConfig: {
            applicationId: process.env.DESCOPE_TELEMETRY_APPLICATION_ID || '',
            identityPoolId:
              process.env.DESCOPE_TELEMETRY_IDENTITY_POOL_ID || '',
            region: process.env.DESCOPE_TELEMETRY_REGION || '',
            sessionSampleRate:
              Number.isFinite(sampleRate) && sampleRate > 0 ? sampleRate : 1,
            ...(process.env.DESCOPE_TELEMETRY_GUEST_ROLE_ARN && {
              guestRoleArn: process.env.DESCOPE_TELEMETRY_GUEST_ROLE_ARN,
            }),
          },
        };

        const cfg: NonNullable<TelemetryConfig> = {
          ...fallback,
          ...beTelemetry,
          rumConfig: {
            ...fallback.rumConfig,
            ...beTelemetry?.rumConfig,
          },
          capture: this.#resolveCapture(beTelemetry?.capture),
        };

        // If enabled but a required RUM credential is missing (empty from both
        // the BE block and the .env fallback), disable rather than hand AwsRum
        // an empty applicationId/identityPoolId/region — that would either
        // throw at construction or silently no-op with no session recorded.
        if (cfg.enabled && !this.#isRumConfigComplete(cfg.rumConfig)) {
          this.logger.warn(
            'Telemetry enabled but rumConfig is incomplete (applicationId/identityPoolId/region required). Skipping initialization.',
          );
          cfg.enabled = false;
        }

        // Safety cap: when no backend telemetry config is present at all,
        // end the session after 5 minutes so dev sessions don't leak. Once
        // the backend owns the config it controls expiration explicitly.
        if (!beTelemetry) {
          cfg.expiration = Date.now() + 5 * 60 * 1000;
          this.logger.debug(
            'No backend telemetry config present; applying a 5-minute safety cap on the session.',
          );
        }

        return cfg;
      }

      /**
       * The backend can't serialize a DOM element reference, so we always
       * inject this.shadowRoot here. Respect an explicit `dom: false`.
       */
      #resolveCapture(
        beCapture: NonNullable<TelemetryConfig>['capture'],
      ): NonNullable<TelemetryConfig>['capture'] {
        // `network: true` = capture ALL network traffic. We used to default to
        // `{ urlFilter: [] }` but the two network paths had inverted semantics
        // for the empty array: the custom NetworkPlugin treated it as
        // "capture everything", while AWS RUM's built-in HTTP telemetry
        // treated it as "capture nothing". `true` is consistent — both paths
        // capture all by default. BE can override with a narrow filter later.
        const domSetting = beCapture?.dom;
        if (domSetting === false) {
          return {
            console: true,
            network: true,
            navigation: true,
            ...beCapture,
            dom: false,
          };
        }
        return {
          console: true,
          network: true,
          navigation: true,
          ...beCapture,
          dom: {
            throttleMs: 2000,
            ...(typeof domSetting === 'object' ? domSetting : {}),
            rootElement: this.shadowRoot as any,
          },
        };
      }

      updateTelemetryContext(context: Record<string, any>) {
        if (this.#telemetryManager) {
          try {
            this.#telemetryManager.updateContext(context);
          } catch (error) {
            // Fail silently - telemetry errors should never break the web-component
            this.logger.error('Error updating telemetry context:', error);
          }
        }
      }

      #telemetryReady: Promise<void> | null = null;

      async init() {
        // super.init() FIRST so downstream mixins finish setup and the
        // SDK's user-facing paint isn't blocked on our CDN load or the
        // `this.config` fetch. Kick off telemetry as its OWN promise chain
        // stored on the instance. NOT awaited here — tests using
        // jest.useFakeTimers can't advance the fetch chain, and blocking on
        // it would freeze the render. Failures are caught so telemetry
        // errors can never break the SDK's boot.
        await super.init?.();
        const sdkVersion = (this as any).sdkVersion || '1.0.0';
        this.#telemetryReady = this.#getTelemetryConfig()
          .then((config) =>
            this.#initializeTelemetry(config as any, {
              projectId: this.projectId,
              flowId: this.flowId,
              sdkVersion,
            }),
          )
          .catch((error) => {
            this.logger.error('Failed to start telemetry:', error);
          });
      }

      // Test hook: resolves once telemetry init has settled (success or
      // caught error). Not part of the public SDK contract.
      get telemetryReady(): Promise<void> {
        return this.#telemetryReady ?? Promise.resolve();
      }

      disconnectedCallback() {
        super.disconnectedCallback?.();
        this.#shutdownTelemetry();
      }
    };
  },
);
